import * as React from 'react';
import { convertFilters } from './utils';
import { Loader } from './Loader';
// import ExportToCsv from "./ExportToCsv";
import { useQuery } from '@apollo/client';
import { AdvancedFilterModal } from './AdvancedFilterModal';
import ReactTable, { ComponentPropsGetterR } from 'react-table';
import { GridError } from './GridError';
import { useSessionStorage } from './utils';
import { CSVTest } from './CSVTest';

export const Grid = (props: any) => {
  const [show, setShow] = React.useState(false);
  const [advanceSearchFilters, setAdvanceSearchFilters] = React.useState<any>([]);
  const [refresh, setRefresh] = React.useState('false');
  let fullQueryTag = props.queryTag !== undefined ? props.queryTag + ': ' + props.queryName : 'Default: ' + props.queryName;
  const [grid, setGrid] = useSessionStorage(fullQueryTag, props);
  let page = grid.page;

  // Controlled filter state. Using `filtered` (instead of `defaultFiltered`)
  // keeps react-table from re-seeding the filter inputs on every render, so the
  // focused input keeps its DOM identity when a refetch toggles `loading`.
  const [filtered, setFiltered] = React.useState<any[]>(() => convertFilters(grid));

  // Debounce the grid state update (and thus the network refetch) so typing in
  // a filter only queries once the user pauses for 600ms.
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // Flags whether the next onFetchData came from filter typing (debounced) vs a
  // sort/page/size change (applied immediately).
  const filterDrivenRef = React.useRef(false);
  React.useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  React.useEffect(() => {
    return () => {
      updateGridMem();
    };
  }, []);

  React.useEffect(() => {
    if (grid.filters === []) page = 0;
    updateGridMem();
  }, [grid.filters]);

  const getQueryVariables = (): any => {
    return {
      csv: false,
      page: grid.page,
      sort: grid.sort,
      field: grid.field,
      amount: grid.amount,
      filters: [...props.globalFilters, ...grid.filters, ...advanceSearchFilters],
    };
  };

  // Always holds the latest query variables. The pager/CSV components are
  // memoized for a stable identity (so headers/filters don't re-render on data
  // updates), so they read current variables through this ref instead of a
  // stale closure.
  const queryVariablesRef = React.useRef(getQueryVariables());
  queryVariablesRef.current = getQueryVariables();

  // Data fetching via the useQuery hook (replaces the legacy <Query> render-prop
  // component). `notifyOnNetworkStatusChange` surfaces background refetch state
  // via `loading`, and `previousData` keeps the last result available while a
  // network-only refetch is in flight — so the table (and its header/filter
  // rows) never unmounts on a data update.
  const { data, previousData, error, loading, refetch } = useQuery(props.query, {
    variables: getQueryVariables(),
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });


  const updateGridMem = () => {
    setGrid(grid);
    if (props.gridMemory) {
      const memory = props.gridMemory.memory;
      memory[props.queryName] = grid;
      props.gridMemory.setMemory(memory);
    }
  };

  const setFiltersForAdavanceSearch = (filters: any) => {
    setGrid({
      ...grid,
      page: 0,
      csv: false,
    });
    setAdvanceSearchFilters(filters);
  };

  // Reads the latest query variables via ref so CSV export reflects current
  // filters/sort/page even though this is called from a memoized component.
  const exportCsvButton = React.useCallback(() => {
    if (props.csv !== undefined) {
      if (props.csv) {
        return <CSVTest grid={queryVariablesRef.current} query={props.query} queryName={props.queryName} />;
      }
      return null;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.csv, props.query, props.queryName]);

  // Components. Memoized so their identity is stable across data updates — this
  // lets react-table skip re-rendering the header/filter row when only the row
  // data changes.
  const PreviousPage = React.useCallback(
    ({ onClick }: { onClick: any }) => {
      return (
        <div className='row mt-1'>
          <div className='col-sm-2'>
            <button className='btn btn-outline-secondary' onClick={() => setShow(true)} title='Advance Search'>
              <i className='fas fa-filter'></i>
            </button>
          </div>
          <div className='col-sm-3 offset-7'>
            <button className='btn btn-block btn-outline-secondary' onClick={onClick} title='Previous Page'>
              <i className='fas fa-angle-left'></i>
            </button>
          </div>
        </div>
      );
    },
    [setShow]
  );

  const NextPage = React.useCallback(
    ({ onClick }: { onClick: any }) => {
      return (
        <div className='row mt-1'>
          <div className='col-sm-3'>
            <button className='btn btn-block btn-outline-secondary' onClick={onClick} title='Next Page'>
              <i className='fas fa-angle-right'></i>
            </button>
          </div>
          <div className='col-sm-2 offset-6'>{exportCsvButton()}</div>
        </div>
      );
    },
    [exportCsvButton]
  );

  const renderLoader = (): React.ReactType => {
    if (props.LoadingComponent) {
      return props.LoadingComponent;
    } else {
      return Loader;
    }
  };
  const loadingComponent = React.useMemo(renderLoader, [props.LoadingComponent]);

  // Build columns (and the accessor -> metadata map) once per column definition
  // change. Keeping `columns` referentially stable prevents react-table from
  // re-rendering the header and filter cells on every data refetch.
  const { columns, map } = React.useMemo(() => {
    const m = new Map<any, { type: string; isFullSearch: boolean }>();
    const cols = props.columns.map((c) => {
      m.set(c.column.accessor, {
        type: c.type,
        isFullSearch: c.isFullSearch ? c.isFullSearch : false,
      });
      return c.column;
    });
    return { columns: cols, map: m };
  }, [props.columns]);

  let rowProps: ComponentPropsGetterR | undefined = undefined;

  const changePage = (newPage) => {
    page = newPage;
  };

  // With fetchPolicy='network-only', `data` is undefined while a refetch is in
  // flight, but `previousData` retains the last result. Preferring `data` and
  // falling back to `previousData` keeps the table rendered (no unmount) during
  // background refetches, so only the row data updates in place.
  const resultData = (data && data[props.queryName]) ?? (previousData && previousData[props.queryName]);

  // Surface a load error only when we have no data to fall back on; otherwise
  // keep showing the existing table while a refetch retries.
  if (error && resultData === undefined) {
    console.log('Grid Error', error);
    return <GridError refetch={() => refetch(getQueryVariables())} />;
  }

  // Show the full-page loader only on the initial load, before any data exists.
  if (resultData === undefined) {
    return <Loader />;
  }

  if (props.rowStyle !== undefined) {
    let fn = props.rowStyle;
    rowProps = (_state, rowInfo, _instance) => {
      if (rowInfo !== undefined) {
        return fn(rowInfo);
      } else {
        return {};
      }
    };
  }

  return (
    <React.Fragment>
      <ReactTable
        manual
        filterable
        resizable={false}
        columns={columns}
        getTrProps={rowProps}
        loading={loading}
        page={page}
        onPageChange={(newPage) => {
          changePage(newPage);
        }}
        showPageJump={false}
        LoadingComponent={loadingComponent}
        filtered={filtered}
        className='-striped -highlight mb-5'
        data={resultData.data}
        defaultSortDesc={false}
        defaultPageSize={grid.amount}
        pages={Math.ceil(resultData.count / grid.amount)}
        NextComponent={NextPage}
        PreviousComponent={PreviousPage}
        onFilteredChange={(nextFiltered) => {
          page = 0;
          // Mark the resulting onFetchData as filter-driven so it debounces.
          filterDrivenRef.current = true;
          setFiltered(nextFiltered);
        }}
        onFetchData={(state) => {
          if (state.filtered !== []) {
            state.page = page;
          } else {
            state.page = 0;
            setGrid({ ...grid, page: 0 });
          }

          let sort = grid.sort;
          let field = grid.field;

          let filters = state.filtered.map((filter) => {
            let filterExtras = map.get(filter.id);
            return {
              id: filter.id,
              operand: 'and',
              value: filter.value,
              type: filterExtras ? filterExtras.type : 'string',
              isFullSearch: filterExtras ? true : false,
              isLike: true,
            };
          });

          filters = [...filters];

          if (state.sorted.length > 0) {
            field = state.sorted[0].id;
            sort = state.sorted[0].desc ? 'DESC' : 'ASC';
          }

          const nextGrid = {
            csv: false,
            sort,
            field,
            filters,
            page: state.page,
            amount: state.pageSize,
          };

          if (debounceRef.current) clearTimeout(debounceRef.current);

          if (filterDrivenRef.current) {
            // Filter typing: wait 600ms after the last keystroke before updating
            // grid state (which triggers the network refetch).
            filterDrivenRef.current = false;
            debounceRef.current = setTimeout(() => {
              setGrid(nextGrid);
            }, 600);
          } else {
            // Sort / page / size changes apply immediately.
            setGrid(nextGrid);
          }
        }}
      />
      <AdvancedFilterModal columns={columns} show={show} setShow={setShow} setFilters={setFiltersForAdavanceSearch} />
    </React.Fragment>
  );
};
